import { toast } from 'react-toastify';

import type { UpdateTemplateFormSchema } from '../validations/edit-template.validation';
import type { CreateTemplateResponse } from './use-create-template.hook';

import { BackendRoute } from '../../../../config/index';
import { QueryKey } from '../../../../constants/index';
import { useAppMutation, useInvalidateQueries, useLocale } from '../../../../hooks/index';
import ApiService from '../../../../services/api-service.util';
import { SigningMode } from '../enums/template.const';
import { getStepThreeFile, getStepThreeFileLink } from '../validations/add-template.validation';

const buildUpdateTemplateBody = (
  data: UpdateTemplateFormSchema,
  fallback: { documentName: string; fileLink: string },
): Record<string, unknown> => {
  const { stepOne, stepTwo, stepThree } = data;
  const fileLink = getStepThreeFileLink(stepThree.file) ?? fallback.fileLink;
  const file = getStepThreeFile(stepThree.file);

  return {
    equityPlanId: stepOne.equityPlan ?? '',
    fileLink,
    documentName: file?.name ?? fallback.documentName,
    name: stepOne.name,
    category: stepOne.category ?? '',
    subCategory: stepOne.subCategory ?? '',
    ...(stepOne.expireDocument && stepOne.expirationPeriod != null
      ? { expirationPeriod: String(stepOne.expirationPeriod) }
      : {}),
    signingOrderMatters: stepTwo.signingMode === SigningMode.SET_ORDER,
    signatoryTypes: stepTwo.signatories,
    fieldMapping: stepThree.fieldMapping ?? {},
    tabs: (stepThree.tabs ?? []).map((tab) => ({
      ...tab,
      signerStakeholderId: tab.value ?? undefined,
    })),
  };
};

export type UseUpdateTemplateParams = {
  onSuccess?: () => void;
  templateDocumentName: string;
  templateFileLink: string;
  templateId: string;
};

export const useUpdateTemplate = ({
  onSuccess,
  templateDocumentName,
  templateFileLink,
  templateId,
}: UseUpdateTemplateParams) => {
  const { invalidateQuery } = useInvalidateQueries(
    QueryKey.GET_TEMPLATES,
    QueryKey.GET_TEMPLATE_BY_ID,
    QueryKey.GET_TEMPLATE_DOCUMENT,
  );
  const { messagesLocale } = useLocale();

  const { isPending, mutate: updateTemplate } = useAppMutation<
    CreateTemplateResponse,
    Error,
    UpdateTemplateFormSchema
  >([QueryKey.CREATE_TEMPLATE, templateId], {
    mutationFn: (data) => {
      const body = buildUpdateTemplateBody(data, {
        documentName: templateDocumentName,
        fileLink: templateFileLink,
      });
      return ApiService.put(
        { endpoint: BackendRoute.TEMPLATES, routePath: [templateId] },
        { body },
      );
    },
    onSuccess: () => {
      toast.success(messagesLocale['TEMPLATE_UPDATED_SUCCESSFULLY']);
      invalidateQuery();
      onSuccess?.();
    },
  });

  return { isPending, updateTemplate };
};
